import React, { useEffect, useState } from 'react';
import { Modal, Form, Col, Container, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { HeaderText } from "../../Controls/HeaderText";
import { useAtom } from "jotai";
import { createApiService } from "../../shared/apiConfig";
import { FeesApi, ServerRequestsFeeInfo, ServerRequestsCreateFeeRequest, ServerRequestsUpdateFeeRequest, DataEntitiesFinanceOperatorFeeCalc, DataEntitiesFinanceOperatorFeeScope, DataEntitiesFinanceOperatorFeePaymentType, DataEntitiesFinancePartner, FacilityApi, OperatorApi } from "../../shared/api/axios-client";
import { Input } from "../../Controls/Input";
import { Select } from "../../Controls/Select";
import { Button } from "../../Controls/Button";
import { toast } from "react-toastify";
import { calculateFeeAmount, formatFeeAmount } from "../../Data/FeeCalculations";
import { HtmlEditor } from "../../Controls/HtmlEditor";


interface FeeEditModalProps {
    show: boolean;
    setShow: (show: boolean) => void;
    fee: ServerRequestsFeeInfo | null;
    onSaved: (updatedFee: ServerRequestsFeeInfo) => void;
    operators: any[];
    facilities: any[];
    isCloneMode?: boolean;
}

export function FeeEditModal({ show, setShow, fee, onSaved, operators, facilities, isCloneMode = false }: FeeEditModalProps) {
    
    const feesService = createApiService(FeesApi);
    const facilityService = createApiService(FacilityApi);
    
    const [operatorFacilities, setOperatorFacilities] = useState<any[]>([]);
    
    const [formData, setFormData] = useState({
        feeName: '',
        amount: 0,
        feeCalc: 0 as DataEntitiesFinanceOperatorFeeCalc,
        feeScope: 0 as DataEntitiesFinanceOperatorFeeScope,
        isAppliedToParker: false,
        operatorId: null as number | null,
        facilityId: null as number | null,
        paymentType: 0 as DataEntitiesFinanceOperatorFeePaymentType,
        description: '',
        receiptDescription: '',
        isPerSpace: false,
        isOptIn: false,
        isDefaultOptIn: false,
        partnerFee: 0,
        partner: null as DataEntitiesFinancePartner | null,
        isFullyRemittedToOperator: false
    });
    
    const [errors, setErrors] = useState<{[key: string]: string}>({});

    // Helper to determine if core fee fields should be disabled (only editable when creating or cloning)
    const isFieldDisabled = () => !!fee && !isCloneMode;


    useEffect(() => {
        if (fee) {
            // Convert from cents to dollars for display, and from decimal to percentage
            let displayAmount = fee.amount || 0;
            if (fee.feeCalc === 0) {
                // Fixed amount: convert cents to dollars (e.g., 2000 -> 20.00)
                displayAmount = (fee.amount || 0) / 100;
            } else if (fee.feeCalc === 1) {
                // Percentage: convert decimal to percentage (e.g., 0.2 -> 20)
                displayAmount = (fee.amount || 0) * 100;
            }
            
            setFormData({
                feeName: isCloneMode ? `${fee.feeName || ''} (Copy)` : fee.feeName || '',
                amount: displayAmount,
                feeCalc: fee.feeCalc || 0,
                feeScope: fee.feeScope || 0,
                isAppliedToParker: fee.isAppliedToParker || false,
                operatorId: isCloneMode ? null : fee.operatorId || null,
                facilityId: isCloneMode ? null : fee.facilityId || null,
                paymentType: fee.paymentType || 0,
                description: fee.description || '',
                receiptDescription: fee.receiptDescription || '',
                isPerSpace: fee.isPerSpace || false,
                // Force IsOptIn to true for ParkGuard fees
                isOptIn: fee.isOptIn || false,
                isDefaultOptIn: fee.isDefaultOptIn || false,
                partnerFee: (fee.partnerFee || 0) / 100, // Convert cents to dollars for display
                partner: fee.partner !== undefined ? fee.partner : null,
                isFullyRemittedToOperator: (fee as any).isFullyRemittedToOperator || false
            });
            
            // Fetch facilities for the existing operator (but not in clone mode since operator is deselected)
            if (fee.operatorId && !isCloneMode) {
                fetchOperatorFacilities(fee.operatorId);
            }
        } else {
           setFormData({
            feeName: '',
            amount: 0,
            feeCalc: 0,
            feeScope: 0,
            isAppliedToParker: false,
            operatorId: null,
            facilityId: null,
            paymentType: 0,
            description: '',
            receiptDescription: '',
            isPerSpace: false,
            isOptIn: false,
            isDefaultOptIn: false,
            partnerFee: 0,
            partner: null,
            isFullyRemittedToOperator: false
        });
            setOperatorFacilities([]);
        }
        setErrors({});
    }, [fee, show, isCloneMode]);

    const fetchOperatorFacilities = async (operatorId: number) => {
        try {
            const response = await facilityService.listFacilitiesLite({
                operatorId: operatorId,
                pageSize: 1000,
                pagination: true
            });
            setOperatorFacilities(response.data?.items || []);
        } catch (error) {
            console.error("Error fetching operator facilities:", error);
            setOperatorFacilities([]);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrors({});

        // Validate required fields
        const newErrors: {[key: string]: string} = {};
        
        if (!formData.feeName?.trim()) {
            newErrors.feeName = 'Fee name is required';
        }
        
        if (!formData.operatorId) {
            newErrors.operatorId = 'Operator is required';
        }
        
        if (formData.amount === null || formData.amount === undefined || formData.amount < 0) {
            newErrors.amount = 'Amount must be a positive number';
        }
        
        if (formData.feeCalc === 1 && formData.amount > 100) {
            newErrors.amount = 'Percentage cannot exceed 100%';
        }

        // Validate that Partner is required when PartnerFee is populated
        if (formData.partnerFee > 0 && !formData.partner && formData.partner !== 0) {
            newErrors.partner = 'Partner is required when Partner Fee is specified';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {

            // Convert amount back to API format
            let apiAmount = formData.amount;
            if (formData.feeCalc === 0) {
                // Fixed amount: convert dollars to cents (e.g., 20.00 -> 2000)
                apiAmount = Math.round(formData.amount * 100);
            } else if (formData.feeCalc === 1) {
                // Percentage: convert percentage to decimal (e.g., 20 -> 0.2)
                apiAmount = formData.amount / 100;
            }

            // Convert partner fee from dollars to cents
            const apiPartnerFee = Math.round(formData.partnerFee * 100);

            let response;
            if (fee && !isCloneMode) {
                // Update existing fee
                const updateRequest: ServerRequestsUpdateFeeRequest = {
                    operatorFeeId: fee.operatorFeeId,
                    feeName: formData.feeName,
                    amount: apiAmount,
                    feeCalc: formData.feeCalc,
                    feeScope: formData.feeScope,
                    isAppliedToParker: formData.isAppliedToParker,
                    paymentType: formData.paymentType,
                    description: formData.description || null,
                    receiptDescription: formData.receiptDescription || null,
                    isPerSpace: formData.isPerSpace,
                    isOptIn: formData.isOptIn,
                    isDefaultOptIn: formData.isDefaultOptIn,
                    partnerFee: apiPartnerFee,
                    partner: formData.partner ?? undefined
                };

                response = await feesService.updateFee({
                    id: fee.operatorFeeId!,
                    serverRequestsUpdateFeeRequest: updateRequest
                });
            } else {
                // Create new fee
                const createRequest: ServerRequestsCreateFeeRequest = {
                    operatorId: formData.operatorId!,
                    feeName: formData.feeName,
                    amount: apiAmount,
                    feeCalc: formData.feeCalc,
                    feeScope: formData.feeScope,
                    isAppliedToParker: formData.isAppliedToParker,
                    facilityId: formData.facilityId,
                    paymentType: formData.paymentType,
                    description: formData.description || null,
                    receiptDescription: formData.receiptDescription || null,
                    isPerSpace: formData.isPerSpace,
                    isOptIn: formData.isOptIn,
                    isDefaultOptIn: formData.isDefaultOptIn,
                    partnerFee: apiPartnerFee,
                    partner: formData.partner ?? undefined,
                    isFullyRemittedToOperator: formData.isFullyRemittedToOperator
                };

                response = await feesService.createFee({
                    serverRequestsCreateFeeRequest: createRequest
                });
            }

            // Check for errors in the response (even with 200 status)
            if (response?.data?.errors && Object.keys(response.data.errors).length > 0) {
                setErrors(response.data.errors);
                return; // Don't show success or close modal
            }

            // Show success toast message
            toast.success(isCloneMode ? 'Fee cloned successfully!' : fee ? 'Fee updated successfully!' : 'Fee created successfully!');
            
            // Pass the updated/new fee data back to the parent
            // Try response.data.item first, fall back to response.data
            const updatedFee = (response.data as any).item || response.data as ServerRequestsFeeInfo;
            onSaved(updatedFee);
        } catch (error: any) {
            console.error("Error saving fee:", error);
            
            // Handle different error response structures
            if (error.response?.data?.errors) {
                // Direct errors object
                setErrors(error.response.data.errors);
            } else if (error.response?.data?.data?.errors) {
                // Nested errors object
                setErrors(error.response.data.data.errors);
            } else if (error.response?.data) {
                // Try to extract errors from the response
                const responseData = error.response.data;
                if (responseData.errors) {
                    setErrors(responseData.errors);
                } else {
                    setErrors({ general: responseData.message || "An error occurred while saving the fee." });
                }
            } else {
                setErrors({ general: "An error occurred while saving the fee." });
            }
        } finally {
            
        }
    };

    const handleClose = () => {
        setShow(false);
        setFormData({
            feeName: '',
            amount: 0,
            feeCalc: 0,
            feeScope: 0,
            isAppliedToParker: false,
            operatorId: null,
            facilityId: null,
            paymentType: 0,
            description: '',
            receiptDescription: '',
            isPerSpace: false,
            isOptIn: false,
            isDefaultOptIn: false,
            partnerFee: 0,
            partner: null,
            isFullyRemittedToOperator: false
        });
        setErrors({});
        setOperatorFacilities([]);
    };

    // Calculate fees based on current form data
    const calculateFees = () => {
        const baseAmount = 10; // $10 base amount
        const baseAmountCents = 1000; // $10 in cents
        
        // Convert form amount to API format for calculation
        let apiAmount = formData.amount;
        if (formData.feeCalc === 0) {
            // Fixed amount: convert dollars to cents
            apiAmount = Math.round(formData.amount * 100);
        } else if (formData.feeCalc === 1) {
            // Percentage: keep as decimal
            apiAmount = formData.amount / 100;
        }
        
        const feeAmount = calculateFeeAmount(apiAmount, formData.feeCalc, baseAmountCents);
        const feeAmountDollars = feeAmount / 100; // Convert back to dollars for display
        
        const facilityCharge = formData.isAppliedToParker ? 0 : feeAmountDollars;
        const parkerCharge = formData.isAppliedToParker ? feeAmountDollars : 0;
        
        return {
            baseAmount,
            feeAmount: feeAmountDollars,
            facilityCharge,
            parkerCharge
        };
    };

    const feeCalculation = calculateFees();

    if (!show) {
        return <></>;
    }

    return (
        <Modal show={show} centered size="lg" onEscapeKeyDown={handleClose} onHide={handleClose}>
            <Modal.Header closeButton>
                <HeaderText>{isCloneMode ? 'Clone Fee' : fee ? 'Edit Fee' : 'Create New Fee'}</HeaderText>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <Row className="mb-0 align-items-center">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Operator *</Form.Label>
                        </Col>
                        <Col md={9}>
                            <Select
                                label=""
                                value={formData.operatorId || ''}
                                    onChange={async (e) => {
                                        const operatorId = e.target.value ? parseInt(e.target.value) : null;
                                        setFormData({
                                            ...formData, 
                                            operatorId,
                                            facilityId: null // Reset facility when operator changes
                                        });
                                        
                                        // Fetch facilities for the selected operator
                                        if (operatorId) {
                                            await fetchOperatorFacilities(operatorId);
                                        } else {
                                            setOperatorFacilities([]);
                                        }
                                    }}
                                isInvalid={!!errors.operatorId}
                                disabled={isFieldDisabled()} // Disable when editing (but not when cloning)
                            >
                                <option value="">Select Operator</option>
                                {operators.map(operator => (
                                    <option key={operator.operatorId} value={operator.operatorId}>
                                        {operator.name}
                                    </option>
                                ))}
                            </Select>
                            {errors.operatorId && (
                                <Form.Text className="d-block text-danger ms-1">
                                    {errors.operatorId}
                                </Form.Text>
                            )}
                        </Col>
                    </Row>

                    <Row  style={{marginTop: '-20px'}} className="align-items-center">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Facility</Form.Label>
                        </Col>
                        <Col md={9}>
                            <Select
                                label=""
                                value={formData.facilityId || ''}
                                onChange={(e) => setFormData({...formData, facilityId: e.target.value ? parseInt(e.target.value) : null})}
                                isInvalid={!!errors.facilityId}
                                disabled={!formData.operatorId || (!!fee && !isCloneMode)} // Disable when editing (but not when cloning) or no operator selected
                            >
                                <option value="">Select Facility (Optional)</option>
                                {operatorFacilities.map(facility => (
                                    <option key={facility.facilityId} value={facility.facilityId}>
                                        {facility.name}
                                    </option>
                                ))}
                            </Select>
                            {errors.facilityId && (
                                <Form.Text className="d-block text-danger ms-1">
                                    {errors.facilityId}
                                </Form.Text>
                            )}
                        </Col>
                    </Row>

                    <Row  style={{marginTop: '-20px'}} className="align-items-center">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Fee Name *</Form.Label>
                        </Col>
                        <Col md={9}>
                            <Input
                                type="text"
                                label=""
                                value={formData.feeName}
                                onChange={(e) => setFormData({...formData, feeName: e.target.value})}
                                isInvalid={!!errors.feeName}
                                placeholder="Enter fee name"
                                maxLength={32}
                            />
                            {errors.feeName && (
                                <Form.Text className="d-block text-danger ms-1">
                                    {errors.feeName}
                                </Form.Text>
                            )}
                        </Col>
                    </Row>

                    <Row  style={{marginTop: '-20px'}} className="align-items-center">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Amount *</Form.Label>
                        </Col>
                        <Col md={9}>
                            <Input
                                type="number"
                                label=""
                                step="0.1"
                                min="0"
                                value={formData.amount}
                                onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})}
                                isInvalid={!!errors.amount}
                                placeholder="Enter amount"
                                disabled={isFieldDisabled()}
                            />
                            {errors.amount && (
                                <Form.Text className="d-block text-danger ms-1">
                                    {errors.amount}
                                </Form.Text>
                            )}
                        </Col>
                    </Row>

                    <Row style={{marginTop: '-20px'}} className="align-items-center">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Fee Scope *</Form.Label>
                        </Col>
                        <Col md={9}>
                            <Select
                                label=""
                                value={formData.feeScope}
                                onChange={(e) => {
                                    const newScope = parseInt(e.target.value) as DataEntitiesFinanceOperatorFeeScope;
                                    setFormData({
                                        ...formData, 
                                        feeScope: newScope,
                                        // Force Credit Card payment type when scope is Reservation
                                        paymentType: newScope === 0 ? 0 : formData.paymentType,
                                        isOptIn: formData.isOptIn
                                    });
                                }}
                                isInvalid={!!errors.feeScope}
                                disabled={isFieldDisabled()}
                            >
                                <option value={0}>Reservation</option>
                                <option value={1}>Subscription</option>
                            </Select>
                            {errors.feeScope && (
                                <Form.Text className="d-block text-danger ms-1">
                                    {errors.feeScope}
                                </Form.Text>
                            )}
                        </Col>
                    </Row>

                    <Row  style={{marginTop: '-20px'}} className="align-items-center">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Calculation Type *</Form.Label>
                        </Col>
                        <Col md={9}>
                            <Select
                                label=""
                                value={formData.feeCalc}
                                onChange={(e) => {
                                    const newFeeCalc = parseInt(e.target.value) as DataEntitiesFinanceOperatorFeeCalc;
                                    setFormData({
                                        ...formData, 
                                        feeCalc: newFeeCalc,
                                        // Turn off isPerSpace if switching to percentage
                                        isPerSpace: newFeeCalc === 1 ? false : formData.isPerSpace
                                    });
                                }}
                                isInvalid={!!errors.feeCalc}
                                disabled={isFieldDisabled()}
                            >
                                <option value={0}>Fixed Amount</option>
                                <option value={1}>Percentage</option>
                            </Select>
                            {errors.feeCalc && (
                                <Form.Text className="d-block text-danger ms-1">
                                    {errors.feeCalc}
                                </Form.Text>
                            )}
                        </Col>
                    </Row>

                    <Row  style={{marginTop: '-20px'}} className="mb-2 align-items-center">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Payment Type *</Form.Label>
                        </Col>
                        <Col md={9}>
                            <Select
                                label=""
                                value={formData.isOptIn ? 2 : formData.paymentType}
                                onChange={(e) => setFormData({...formData, paymentType: parseInt(e.target.value) as DataEntitiesFinanceOperatorFeePaymentType})}
                                isInvalid={!!errors.paymentType}
                                disabled={formData.feeScope === 0 || formData.isOptIn} // Disable when scope is Reservation or Opt-In
                            >
                                {formData.isOptIn ? (
                                    <option value={2}>Any</option>
                                ) : (
                                    <>
                                        <option value={0}>Credit Card</option>
                                        <option value={1} disabled={formData.feeScope === 0}>ACH</option>
                                    </>
                                )}
                            </Select>
                            {formData.feeScope === 0 && (
                                <Form.Text className="d-block text-muted ms-1">
                                
                                    ACH is not available for Reservations
                                </Form.Text>
                            )}
                            {errors.paymentType && (
                                <Form.Text className="d-block text-danger ms-1">
                                    {errors.paymentType}
                                </Form.Text>
                            )}
                        </Col>
                    </Row>
                  
                    <Row className="mb-3 align-items-center">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Applied to Parkers</Form.Label>
                        </Col>
                        <Col md={9}>
                            <div className="d-flex align-items-center gap-2">
                                <Form.Check
                                    type="checkbox"
                                    checked={formData.isAppliedToParker}
                                    onChange={(e) => setFormData({...formData, isAppliedToParker: e.target.checked})}
                                    disabled={formData.isOptIn || isFieldDisabled()}
                                />
                                {formData.isOptIn && (
                                    <span className="text-muted small">
                                        (Required for Opt-In fees)
                                    </span>
                                )}
                            </div>
                        </Col>
                    </Row>     

                    <Row className="mb-3 align-items-center">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Is Per Space</Form.Label>
                            <OverlayTrigger placement="right" overlay={<Tooltip id="tip-is-per-space">When enabled, the fee is applied to each space.</Tooltip>}>
                                <span role="img" aria-label="info"><InfoCircleFill className="ms-1 icon-shift-up" size={14} /></span>
                            </OverlayTrigger>
                        </Col>
                        <Col md={9}>
                            <div className="d-flex align-items-center gap-2">
                                <Form.Check
                                    type="checkbox"
                                    checked={formData.isPerSpace}
                                    onChange={(e) => setFormData({...formData, isPerSpace: e.target.checked})}
                                    disabled={formData.feeCalc !== 0 || isFieldDisabled()}
                                />
                                {formData.feeCalc !== 0 && (!fee || isCloneMode) && (
                                    <span className="text-muted small">
                                        (Only available for Fixed Amount fees)
                                    </span>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Row className="mb-3 align-items-center">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Is Opt-In</Form.Label>
                        </Col>
                        <Col md={9}>
                            <div className="d-flex align-items-center gap-2">
                                                                 <Form.Check
                                     type="checkbox"
                                     checked={formData.isOptIn}
                                     onChange={(e) => setFormData({
                                         ...formData, 
                                         isOptIn: e.target.checked,
                                         // Force Applied to Parker when Is Opt-In is selected
                                         isAppliedToParker: e.target.checked ? true : formData.isAppliedToParker,
                                         // If opting in, set payment type to Any (2). If opting out and current is Any, reset to Credit Card (0)
                                         paymentType: e.target.checked ? 2 : (formData.paymentType === 2 ? 0 : formData.paymentType)
                                     })}
                                     disabled={(formData.feeScope as number) === 2 || isFieldDisabled()}
                                 />
                                
                            </div>
                        </Col>
                    </Row>

                    {formData.isOptIn && (
                        <>
                    <Row className="mb-3">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Description
                                <OverlayTrigger placement="right" overlay={<Tooltip id="tip-description">Shown to users to explain the opt-in fee details.</Tooltip>}>
                                    <span role="img" aria-label="info"><InfoCircleFill className="ms-1 icon-shift-up" size={14} /></span>
                                </OverlayTrigger>
                            </Form.Label>
                        </Col>
                        <Col md={9}>
                            <HtmlEditor
                                value={formData.description}
                                onChange={(html) => setFormData({...formData, description: html})}
                                placeholder="Enter fee description..."
                                isInvalid={!!errors.description}
                            />
                            {errors.description && (
                                <Form.Text className="d-block text-danger ms-1">
                                    {errors.description}
                                </Form.Text>
                            )}
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Receipt Description
                                <OverlayTrigger placement="right" overlay={<Tooltip id="tip-receipt-description">Appears on the customer receipt to describe this fee.</Tooltip>}>
                                    <span role="img" aria-label="info"><InfoCircleFill className="ms-1 icon-shift-up" size={14} /></span>
                                </OverlayTrigger>
                            </Form.Label>
                        </Col>
                        <Col md={9}>
                            <HtmlEditor
                                value={formData.receiptDescription}
                                onChange={(html) => setFormData({...formData, receiptDescription: html})}
                                placeholder="Enter receipt description..."
                                isInvalid={!!errors.receiptDescription}
                            />
                            {errors.receiptDescription && (
                                <Form.Text className="d-block text-danger ms-1">
                                    {errors.receiptDescription}
                                </Form.Text>
                            )}
                        </Col>
                    </Row>

                   
                 
                         <Row className="mb-3 align-items-center">
                             <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Is Default Opt-In
                                <OverlayTrigger placement="right" overlay={<Tooltip id="tip-is-default-opt-in">If enabled, this opt-in fee starts turned on by default for new eligible users.</Tooltip>}>
                                    <span role="img" aria-label="info"><InfoCircleFill className="ms-1 icon-shift-up" size={14} /></span>
                                </OverlayTrigger>
                            </Form.Label>
                             </Col>
                             <Col md={9}>
                                <Form.Check
                                    type="checkbox"
                                    checked={formData.isDefaultOptIn}
                                    onChange={(e) => setFormData({...formData, isDefaultOptIn: e.target.checked})}
                                />
                             </Col>
                         </Row>

                         <Row className="mb-3 align-items-center">
                             <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Fully Remitted To Operator
                                <OverlayTrigger placement="right" overlay={<Tooltip id="tip-fully-remitted">When enabled, the entire fee amount is remitted to the operator.</Tooltip>}>
                                    <span role="img" aria-label="info"><InfoCircleFill className="ms-1" size={14} /></span>
                                </OverlayTrigger>
                            </Form.Label>
                             </Col>
                             <Col md={9}>
                                <Form.Check
                                    type="checkbox"
                                    checked={formData.isFullyRemittedToOperator}
                                    onChange={(e) => setFormData({...formData, isFullyRemittedToOperator: e.target.checked})}
                                    disabled={!!fee && !isCloneMode}
                                />
                             </Col>
                         </Row>
                         </>
                     )}

                    <Row className="mb-3 align-items-center">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Partner Fee
                                <OverlayTrigger placement="right" overlay={<Tooltip id="tip-partner-fee">For reporting purposes only. This is the dollar amount remitted to the selected partner per transaction.</Tooltip>}>
                                    <span role="img" aria-label="info"><InfoCircleFill className="ms-1 icon-shift-up" size={14} /></span>
                                </OverlayTrigger>
                            </Form.Label>
                        </Col>
                         <Col md={9}>
                             <Input
                                 type="number"
                                 label=""
                                 step="0.01"
                                 min="0"
                                 value={formData.partnerFee}
                                 onChange={(e) => setFormData({...formData, partnerFee: parseFloat(e.target.value) || 0})}
                                 isInvalid={!!errors.partnerFee}
                                 placeholder="Enter partner fee amount"
                             />
                           
                             {errors.partnerFee && (
                                 <Form.Text className="d-block text-danger ms-1">
                                     {errors.partnerFee}
                                 </Form.Text>
                             )}
                         </Col>
                     </Row>

                     <Row className="mb-3 align-items-center">
                        <Col md={3}>
                            <Form.Label className="mb-0 fw-bold">Partner
                                <OverlayTrigger placement="right" overlay={<Tooltip id="tip-partner">Choose a partner to attribute the partner fee to.</Tooltip>}>
                                    <span role="img" aria-label="info"><InfoCircleFill className="ms-1 icon-shift-up" size={14} /></span>
                                </OverlayTrigger>
                            </Form.Label>
                        </Col>
                         <Col md={9}>
                             <Select
                                 label=""
                                 value={formData.partner ?? ''}
                                 onChange={(e) => setFormData({...formData, partner: e.target.value ? parseInt(e.target.value) as DataEntitiesFinancePartner : null})}
                                 isInvalid={!!errors.partner}
                                 disabled={isFieldDisabled()}
                             >
                                 <option value="">Select Partner (Optional)</option>
                                 <option value={0}>ParkGuard</option>
                             </Select>
                            
                             {errors.partner && (
                                 <Form.Text className="d-block text-danger ms-1">
                                     {errors.partner}
                                 </Form.Text>
                             )}
                         </Col>
                     </Row>
                       
                    {Object.keys(errors).length > 0 && (
                        <Row className="mb-3">
                            <Col>
                                <div className="alert alert-danger">
                                    {Object.values(errors).map((error, index) => (
                                        <div key={index}>{error}</div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Fee Calculation Preview */}
                    <Row className="mb-4">
                        <Col>
                            <div className="border rounded p-3 bg-light">
                                <h6 className="text-muted mb-3">
                                    <i className="bi bi-calculator"></i>
                                    Fee Calculation Preview (Based on $10 Base Amount)
                                </h6>
                                <Row>
                                    <Col md={8}>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-muted">Base Amount:</span>
                                            <span className="fw-bold">${feeCalculation.baseAmount.toFixed(2)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-muted">Fee Amount:</span>
                                            <span className="fw-bold text-primary">
                                               
                                                {formData.feeCalc === 1  && (
                                                    <span>
                                                        {formData.amount}%
                                                    </span>
                                                )}
                                                {formData.feeCalc === 0 && (
                                                    <span>${feeCalculation.feeAmount.toFixed(2)}</span>
                                                )}
                                            </span>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                            <span className="fw-bold">Total Amount:</span>
                                            <span className="fw-bold fs-5 text-dark">
                                                ${(feeCalculation.baseAmount + feeCalculation.feeAmount).toFixed(2)}
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                      
                                       
                                    </Col>
                                </Row>
                                <div className="pt-2">
                                    <small >
                                       **
                                        {formData.isAppliedToParker 
                                            ? "Fee will be charged to the parker" 
                                            : "Fee will be charged to the facility"
                                        }
                                    </small>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            type="button"
                           
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit"
                            className="btn-main"
                        >
                            {isCloneMode || !fee ? 'Create Fee' :  'Update Fee' }
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
} 